/**
 * 首页右上角、入口栏下方的临时内容入口。播放器有曲目时显示圆形音乐按钮，
 * 当天有站外消息时在它右边显示平台气泡；点开消息后使用回忆点滴同一个
 * ReadOnlyConversationLog 回放对话。
 */

import { useEffect, useState } from "react";
import { IconComment, IconMusic } from "../../components/Icons";
import { useMusicPlayer } from "../../components/MusicPlayer";
import { fetchTodayChannelEncounters } from "../../services/channel-encounter-api";
import { useResource } from "../../state/resource";
import { useAppearance } from "../../state/session";
import { ChannelConversationSheet } from "./ChannelConversationSheet";
import "../pages.css";
import "./channel.css";

export function TodayChannelBubbles() {
  const appearance = useAppearance();
  const musicPlayer = useMusicPlayer();
  const encounters = useResource(fetchTodayChannelEncounters, []);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);

  // 用户从 QQ / 直播 App 切回来时，页面可能没有重新挂载；重新聚焦就静默刷新。
  useEffect(() => {
    const refresh = () => encounters.refresh();
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [encounters.refresh]);

  const platforms =
    encounters.state.phase === "ready" ? encounters.state.data.platforms : [];
  const selected =
    platforms.find((platform) => platform.providerKey === selectedProvider) ?? null;

  if (!musicPlayer.track && platforms.length === 0) return null;

  return (
    <>
      <div className="channel-bubbles" aria-label="当前内容入口">
        {musicPlayer.track ? (
          <button
            type="button"
            className={`channel-bubble channel-bubble--music${
              musicPlayer.isPlaying ? " is-playing" : ""
            }`}
            onClick={musicPlayer.openPanel}
            aria-label={`打开播放器：${musicPlayer.track.title}`}
            title={musicPlayer.track.title}
          >
            <IconMusic size={17} />
            {musicPlayer.isPlaying ? (
              <span className="channel-bubble__pulse" aria-hidden />
            ) : null}
          </button>
        ) : null}

        {platforms.length > 0 ? (
          <div className="channel-bubbles__platforms" aria-label="今天在其他平台的消息">
            {platforms.map((platform) => (
              <button
                key={platform.providerKey}
                type="button"
                className="channel-bubble"
                onClick={() => setSelectedProvider(platform.providerKey)}
                aria-label={`${platform.platformLabel}今天有${platform.messageCount}条消息，点击展开`}
                title={`${platform.platformLabel} · 今天 ${platform.messageCount} 条`}
              >
                <IconComment size={15} />
                <span className="channel-bubble__label">
                  {platform.platformLabel}（{countLabel(platform.messageCount)}）
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <ChannelConversationSheet
        open={selected !== null}
        title={selected ? `今天在 ${selected.platformLabel}` : "今天的相遇"}
        threads={selected?.threads ?? []}
        hasEarlier={selected?.hasEarlier ?? false}
        speakerName={appearance.name}
        avatarUrl={appearance.avatarUrl}
        onClose={() => setSelectedProvider(null)}
      />
    </>
  );
}

function countLabel(count: number): string {
  return count > 999 ? "999+" : String(count);
}
